import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";

/**
 * Phase 8 — 🎯 Beam Box Quests Panel
 * ---------------------------------
 * Self-contained panel — fetches & polls /api/quests/me independently of
 * its parent. Memoized + isolated so it never causes the parent
 * LiveRoomPage to re-render, which means the live WebSocket and the
 * <audio> elements above it are never disturbed.
 *
 * Public API:
 *   <QuestsPanel
 *     enabled={!!me}                  // anonymous → panel hidden
 *     refreshTick={tickCounter}       // bump to force a refresh after
 *                                     // an in-room gift / reaction
 *   />
 */
const POLL_MS = 30_000;

const QuestsPanel = ({ enabled = true, refreshTick = 0 }) => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);
  const lastFetchedAt = useRef(0);

  const fetchState = useCallback(async () => {
    if (!enabled) return;
    try {
      const r = await api.get("/quests/me");
      setState(r.data || null);
      lastFetchedAt.current = Date.now();
    } catch (_e) {
      // Silent — panel still degrades gracefully.
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  // Initial + periodic poll
  useEffect(() => {
    if (!enabled) { setLoading(false); return; }
    fetchState();
    const id = setInterval(fetchState, POLL_MS);
    return () => clearInterval(id);
  }, [enabled, fetchState]);

  // External refresh — debounced so spamming reactions doesn't blast the
  // backend. Ignores ticks within 1.2s of a previous fetch.
  useEffect(() => {
    if (!enabled) return;
    if (!refreshTick) return;
    if (Date.now() - lastFetchedAt.current < 1200) return;
    fetchState();
  }, [refreshTick, enabled, fetchState]);

  const claim = async (questId) => {
    if (claiming) return;
    setClaiming(questId);
    try {
      const r = await api.post(`/quests/me/claim/${questId}`);
      if (r.data?.reward) {
        const xp = r.data.reward.xp || 0;
        const coins = r.data.reward.coins || 0;
        const parts = [];
        if (coins) parts.push(`+${coins.toLocaleString()}c`);
        if (xp) parts.push(`+${xp} XP`);
        toast.success(`Quest reward claimed! ${parts.join(" · ")}`);
      }
      // Use the response payload directly so the UI updates without a
      // round-trip to /api/quests/me.
      if (r.data?.quests && r.data?.level) {
        setState((prev) => prev ? {
          ...prev,
          quests: r.data.quests,
          level: r.data.level,
        } : prev);
      } else {
        await fetchState();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't claim reward");
    } finally {
      setClaiming(null);
    }
  };

  if (!enabled) return null;
  if (loading && !state) {
    return (
      <Card
        className="p-3 bg-white/5 border-white/10"
        data-testid="quests-panel-loading"
      >
        <p className="text-pink-200/50 text-xs text-center py-3">Loading quests…</p>
      </Card>
    );
  }
  if (!state) return null;

  const level = state.level || {};
  const quests = state.quests || [];
  const xp = Number(level.xp || 0);
  const nextAt = level.next_at;
  const prevTier = nextAt ? Math.max(0, xp - (level.to_next || 0) - (xp - 0)) : 0; // unused safe-guard
  // Compute progress within the CURRENT tier (xp from this tier's
  // threshold towards next_at). Falls back to 100% at max tier.
  const tierStart = nextAt && level.to_next != null ? xp - (nextAt - level.to_next - xp) : xp;
  let pct = 100;
  if (nextAt) {
    // Find current threshold = xp - ((nextAt - xp) - to_next  ). Simpler:
    // Use to_next vs delta-to-next-tier across tier span. We don't expose
    // tier span explicitly; approximate visually by xp / nextAt clamped.
    pct = Math.min(100, Math.max(0, Math.round((xp / nextAt) * 100)));
  }

  return (
    <Card
      className="p-3 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/30"
      data-testid="quests-panel"
    >
      {/* Level header */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-2xl" data-testid="quests-level-emoji">{level.emoji || "🎧"}</span>
          <div className="min-w-0">
            <p
              className="text-[10px] uppercase tracking-wider text-purple-200/70 font-bold truncate"
              data-testid="quests-level-label"
            >
              Lv {level.level || 1} · {level.label || "Listener"}
            </p>
            <p className="text-[11px] text-pink-100/80" data-testid="quests-level-xp">
              {xp.toLocaleString()} XP
              {nextAt ? <span className="text-pink-200/50"> · {Math.max(0, level.to_next || 0)} to next</span> : <span className="text-amber-200/80"> · MAX</span>}
            </p>
          </div>
        </div>
      </div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden mb-3">
        <div
          className="h-full bg-gradient-to-r from-purple-400 to-pink-400 transition-all duration-500"
          style={{ width: `${pct}%` }}
          data-testid="quests-level-bar"
        />
      </div>

      <p className="text-[10px] uppercase tracking-wider text-purple-200/70 font-bold mb-2">
        🎯 Quests
      </p>

      <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1" data-testid="quests-list">
        {quests.length === 0 && (
          <p className="text-pink-200/50 text-xs text-center py-3">No quests today.</p>
        )}
        {quests.map((q) => {
          const ratio = q.target > 0 ? Math.min(1, q.progress / q.target) : 0;
          const pctQ = Math.round(ratio * 100);
          const claimable = !!q.claimable;
          const claimed = !!q.claimed;
          const completed = !!q.completed;
          return (
            <div
              key={q.quest_id}
              className={`p-2 rounded-lg border ${
                claimable
                  ? "bg-amber-500/15 border-amber-400/50 shadow-md shadow-amber-500/10"
                  : claimed
                  ? "bg-emerald-500/10 border-emerald-500/30 opacity-80"
                  : "bg-white/5 border-white/10"
              }`}
              data-testid={`quest-${q.quest_id}`}
            >
              <div className="flex items-center gap-2">
                <span className="text-lg" data-testid={`quest-icon-${q.quest_id}`}>
                  {q.icon || "🎯"}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white font-semibold truncate" data-testid={`quest-title-${q.quest_id}`}>
                    {q.title}
                  </p>
                  <p className="text-[10px] text-pink-200/60 truncate">
                    {q.scope === "weekly" ? "Weekly" : "Daily"}
                    {" · "}
                    {q.reward?.coins ? <span className="text-amber-200">+{q.reward.coins}c </span> : null}
                    {q.reward?.xp ? <span className="text-purple-200">+{q.reward.xp}xp</span> : null}
                  </p>
                </div>
                <span
                  className="text-[10px] text-white/80 font-mono shrink-0"
                  data-testid={`quest-progress-text-${q.quest_id}`}
                >
                  {Math.min(q.progress, q.target)}/{q.target}
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-white/5 overflow-hidden mt-1.5">
                <div
                  className={`h-full transition-all duration-500 ${
                    claimed
                      ? "bg-emerald-500/60"
                      : completed
                      ? "bg-amber-400"
                      : "bg-gradient-to-r from-purple-400 to-pink-400"
                  }`}
                  style={{ width: `${pctQ}%` }}
                  data-testid={`quest-bar-${q.quest_id}`}
                />
              </div>
              {claimable && (
                <button
                  onClick={() => claim(q.quest_id)}
                  disabled={claiming === q.quest_id}
                  className="mt-1.5 w-full py-1 rounded-md bg-gradient-to-r from-amber-400 to-pink-500 text-black text-[11px] font-bold hover:from-amber-300 hover:to-pink-400 disabled:opacity-50 transition-colors"
                  data-testid={`quest-claim-${q.quest_id}`}
                >
                  {claiming === q.quest_id ? "Claiming…" : "Claim reward"}
                </button>
              )}
              {claimed && (
                <p
                  className="mt-1 text-[10px] text-emerald-200/80 text-center"
                  data-testid={`quest-claimed-${q.quest_id}`}
                >
                  ✓ Claimed
                </p>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default QuestsPanel;
